import { createRouter, createWebHistory, type RouteRecordRaw, RouterView } from 'vue-router';
import { setupGuards } from '@/router/guards';

// Grouping-only records still need a component to render their children through.
const RouterViewPassthrough = RouterView;

const guestRoutes: RouteRecordRaw = {
    path: '/',
    component: () => import('@/layouts/Guest.vue'),
    meta: { guest: true },
    children: [
        { path: 'login', name: 'login', component: () => import('@/Pages/Auth/Login.vue') },
        { path: 'register', name: 'register', component: () => import('@/Pages/Auth/Register.vue') },
        { path: 'password/reset', name: 'password.email', component: () => import('@/Pages/Auth/PasswordEmail.vue') },
        { path: 'password/reset/:token', name: 'password.reset', component: () => import('@/Pages/Auth/PasswordReset.vue') },
    ],
};

const publicRoutes: RouteRecordRaw = {
    path: '/',
    component: () => import('@/layouts/Empty.vue'),
    children: [
        { path: 'terms', name: 'terms', component: () => import('@/Pages/Terms.vue') },
        { path: 'error', name: 'error', component: () => import('@/Pages/ErrorPage.vue') },
    ],
};

const settingsRoutes: RouteRecordRaw[] = [
    { path: '', redirect: { name: 'settings.profile' } },
    { path: 'profile', name: 'settings.profile', component: () => import('@/Pages/Settings/Profile.vue') },
    { path: 'security', name: 'settings.security', component: () => import('@/Pages/Settings/Security.vue') },
    { path: 'advanced', name: 'settings.advanced', component: () => import('@/Pages/Settings/Advanced.vue') },
    { path: 'api', name: 'settings.api', component: () => import('@/Pages/Settings/ApiTokens.vue') },
    { path: 'subscription', name: 'settings.subscription', component: () => import('@/Pages/Settings/Subscription.vue') },
    { path: 'payment-method', name: 'settings.paymentMethod', component: () => import('@/Pages/Settings/PaymentMethod.vue') },
    { path: 'invoices', name: 'settings.invoices', component: () => import('@/Pages/Settings/Invoices.vue') },
    { path: 'teams', name: 'settings.teams', component: () => import('@/Pages/Settings/Teams.vue') },
];

const authRoutes: RouteRecordRaw = {
    path: '/',
    component: () => import('@/layouts/Content.vue'),
    meta: { requiresAuth: true },
    children: [
        { path: '', redirect: { name: 'dashboard' } },
        { path: 'home', name: 'dashboard', component: () => import('@/Pages/Dashboard.vue') },
        {
            path: 'settings',
            component: RouterViewPassthrough,
            children: settingsRoutes,
        },
        {
            path: 'settings/teams/:teamId',
            name: 'settings.team',
            component: () => import('@/Pages/Settings/TeamSettings.vue'),
            props: true,
        },
        { path: 'teams/missing', name: 'teams.missing', component: () => import('@/Pages/MissingTeam.vue') },
        {
            path: 'affiliates',
            component: RouterViewPassthrough,
            children: [
                { path: '', name: 'affiliates', component: () => import('@/Pages/Affiliates/Home.vue') },
                { path: 'transactions', name: 'affiliates.transactions', component: () => import('@/Pages/Affiliates/Transactions.vue') },
                { path: 'withdraw', name: 'affiliates.withdraw', component: () => import('@/Pages/Affiliates/Withdraw.vue') },
            ],
        },
        {
            path: 'spark/kiosk',
            component: RouterViewPassthrough,
            meta: { requiresDeveloper: true },
            children: [
                { path: '', redirect: { name: 'kiosk.metrics' } },
                { path: 'metrics', name: 'kiosk.metrics', component: () => import('@/Pages/Kiosk/Metrics.vue') },
                { path: 'users', name: 'kiosk.users', component: () => import('@/Pages/Kiosk/Users.vue') },
                { path: 'users/:userId', name: 'kiosk.userProfile', component: () => import('@/Pages/Kiosk/UserProfile.vue'), props: true },
                { path: 'announcements', name: 'kiosk.announcements', component: () => import('@/Pages/Kiosk/Announcements.vue') },
                { path: 'affiliates', name: 'kiosk.affiliates', component: () => import('@/Pages/Kiosk/Affiliates.vue') },
            ],
        },
    ],
};

const verifyRoute: RouteRecordRaw = {
    path: '/email/verify',
    name: 'verification.notice',
    component: () => import('@/Pages/Auth/VerifyEmail.vue'),
    meta: { requiresAuth: true, skipVerified: true },
};

const routes: RouteRecordRaw[] = [
    guestRoutes,
    publicRoutes,
    verifyRoute,
    authRoutes,
    { path: '/:pathMatch(.*)*', name: 'notFound', component: () => import('@/Pages/ErrorPage.vue'), props: { status: '404' } },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior: () => ({ top: 0 }),
});

setupGuards(router);

const CHUNK_RELOAD_KEY = 'vite_chunk_reload_attempted';

router.onError((error: unknown, to) => {
    const message = error instanceof Error ? error.message : String(error);
    const isChunkError = message.includes('Failed to fetch dynamically imported module') // Chrome
        || message.includes('Importing a module script failed') // Safari
        || message.includes('error loading dynamically imported module') // Firefox
        || message.includes('Couldn\'t resolve component');

    if (!isChunkError) return;

    // One reload recovers a stale chunk reference after a deploy; a second failure
    // means the asset genuinely is not there, so stop looping and show the error page.
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) != null) {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        router.push({ name: 'error', query: { status: '503' } });
        return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    window.location.href = to.fullPath;
});

router.afterEach(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

export default router;
